import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import {
  Wrench, Ban, ShieldCheck, MapPin, RefreshCw, CheckCircle2, ShieldOff,
  Plus, Pencil, Trash2,
} from "lucide-react";
import PanelState, { extractApiError } from "@/components/PanelState";
import { AltKeyBadge, RateOverrideBadge, useAltKeyStatus } from "@/components/AltKeyBadge";
import { useAuth } from "@/lib/auth";
import {
  LICENSE_LABEL, TIER_TONE,
  TechProfileFormModal, TechProfileDeleteConfirm,
} from "@/components/TechProfileFormModal";

// ============================================================================
// Tenant-scoped Tech Profiles (2026-07-31 revised).
// call_center gained full CRUD on 2026-07-31 (previously read-only). The page
// now renders New/Edit/Delete controls for platform_admin, owner, AND
// call_center. `administrative` still sees the page but with no write controls
// (backend also returns 403 on their POST/PATCH/DELETE — matches per-page
// deliberate blast-radius decision from the previous session).
//
// avoid_tags mutations are audit-logged server-side under the caller's real
// user (get_current_user resolves the JWT), so writes from a CSR here land in
// admin_audit_log tagged with their user_id/email — NOT `system_seed_migration`.
// ============================================================================

const TIER_LABEL = { top: "Top", mid: "Mid", developing: "Developing" };

// Any role that can WRITE tech profiles per the API contract. Mirrors the
// server's require_roles() list on POST/PATCH/DELETE /tech-profiles.
const WRITE_ROLES = new Set(["platform_admin", "owner", "call_center"]);


export default function TechProfilesRead() {
  const { slug } = useParams();
  const { user } = useAuth();
  const canWrite = user && WRITE_ROLES.has(user.role);

  const [rows, setRows] = useState([]);
  const [load, setLoad] = useState({ status: "loading", error: null });
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const altKey = useAltKeyStatus(slug);

  const load_ = useCallback(async () => {
    setLoad({ status: "loading", error: null });
    try {
      const { data } = await api.get("/tech-profiles", { params: { tenant_slug: slug } });
      setRows(Array.isArray(data) ? data : []);
      setLoad({ status: "ok", error: null });
    } catch (err) {
      setRows([]);
      setLoad({ status: "error", error: extractApiError(err) });
    }
  }, [slug]);

  useEffect(() => { load_(); }, [load_]);

  const visible = rows.filter((r) => showInactive ? true : r.active !== false);
  // avoid_tags visibility is a SERVER-side rule (Option B): stripped from the
  // API response for roles outside {platform_admin, owner, call_center}. We
  // just render whatever came back — no client-side rechecks.
  const anyRowHasAvoidTags = rows.some((r) => Array.isArray(r.avoid_tags));

  return (
    <div className="space-y-5" data-testid="tenant-tech-profiles-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[.15em] text-[color:var(--cc-ink-muted)] font-semibold">
            Dispatch reference
          </div>
          <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] mt-1 inline-flex items-center gap-2.5">
            <Wrench size={22} strokeWidth={1.6} className="text-[#0072bc]" />
            Tech Profiles
          </h1>
          <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-1 max-w-3xl">
            {canWrite
              ? "Field-team roster — tier, territory, license, strengths, and known avoid-tags. Edits by CSR or admin apply immediately and are audit-logged."
              : "Read-only roster of the field team — tier, territory, license, and known strengths."}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-[12.5px] cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="tenant-tech-profiles-toggle-inactive"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="accent-[#0072bc]"
            />
            Show inactive
          </label>
          <span className="text-[color:var(--cc-ink-muted)] text-[12.5px]">
            {visible.length} of {rows.length}
          </span>
          <button
            onClick={load_}
            data-testid="tenant-tech-profiles-refresh"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 border border-[#e7e9ee] rounded-lg hover:bg-[#f5f6f8]"
          >
            <RefreshCw size={14} strokeWidth={1.8} /> Refresh
          </button>
          {canWrite && (
            <button
              onClick={() => setEditing({})}
              data-testid="tenant-tech-profiles-new-btn"
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 bg-[#0072bc] text-white rounded-lg hover:bg-[#005a95]"
            >
              <Plus size={14} strokeWidth={2} /> New tech profile
            </button>
          )}
        </div>
      </header>

      {load.status !== "ok" || visible.length === 0 ? (
        <PanelState
          status={load.status} error={load.error}
          isEmpty={visible.length === 0}
          emptyTitle="No tech profiles yet"
          emptyBody={canWrite
            ? 'Click "New tech profile" to add your first technician.'
            : "Ask a CSR, owner, or platform admin to add technicians."}
          testIdPrefix="tenant-tech-profiles"
        />
      ) : (
        <div className="border border-[#e7e9ee] rounded-lg overflow-x-auto bg-white">
          <table className="w-full text-[13px] min-w-[900px]">
            <thead className="bg-[#f5f6f8] text-[11px] uppercase tracking-wider text-[color:var(--cc-ink-muted)]">
              <tr>
                <th className="text-left px-3 py-2.5 font-semibold">Name</th>
                <th className="text-left px-3 py-2.5 font-semibold">Tier</th>
                <th className="text-left px-3 py-2.5 font-semibold">License</th>
                <th className="text-left px-3 py-2.5 font-semibold">Territory</th>
                <th className="text-left px-3 py-2.5 font-semibold">Relationship tags</th>
                {anyRowHasAvoidTags && (
                  <th className="text-left px-3 py-2.5 font-semibold">Avoid tags</th>
                )}
                <th className="text-left px-3 py-2.5 font-semibold">Status</th>
                {canWrite && (
                  <th className="text-right px-3 py-2.5 font-semibold">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id} data-testid={`tenant-tech-profile-row-${r.id}`} className="border-t border-[#e7e9ee] align-top">
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-[#0e1420]">{r.name}</div>
                    <AltKeyBadge status={altKey[r.id]} testId={`tenant-tech-profile-altkey-${r.id}`} />
                    <RateOverrideBadge pct={r.fixed_commission_pct} testId={`tenant-tech-profile-rate-override-${r.id}`} />
                    {r.communication_style && (
                      <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-0.5 max-w-[260px]">
                        {r.communication_style}
                      </div>
                    )}
                    {r.notes && (
                      <div className="text-[11.5px] text-[#4b5563] mt-1.5 max-w-[380px] leading-relaxed">
                        {r.notes}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] rounded-full border font-semibold uppercase tracking-wider ${TIER_TONE[r.tier] || "bg-[#f5f6f8] text-[#4b5563] border-[#e7e9ee]"}`}>
                      {TIER_LABEL[r.tier] || r.tier}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">{LICENSE_LABEL[r.license] || r.license}</td>
                  <td className="px-3 py-2.5">
                    <div className="inline-flex items-center gap-1 text-[12.5px]">
                      <MapPin size={11} strokeWidth={1.8} />
                      {(r.home_territory || []).join(", ") || "—"}
                    </div>
                    {r.cross_territory && (
                      <div className="text-[11px] text-[#0072bc] font-semibold mt-0.5">cross-territory OK</div>
                    )}
                    {r.alt_availability && (
                      <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-0.5 max-w-[220px]">
                        {r.alt_availability}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {(r.relationship_tags || []).length === 0 ? (
                      <span className="text-[color:var(--cc-ink-muted)]">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(r.relationship_tags || []).map((t) => (
                          <span key={t} className="inline-flex items-center px-1.5 py-0.5 text-[11px] rounded-full bg-[#e7f4fb] text-[#1c6a99] border border-[#c5e0ee]">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  {anyRowHasAvoidTags && (
                    <td className="px-3 py-2.5">
                      {(r.avoid_tags || []).length === 0 ? (
                        <span className="text-[color:var(--cc-ink-muted)]">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(r.avoid_tags || []).map((t) => (
                            <span key={t} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded-full bg-[#fff5f5] text-[#7a1c1c] border border-[#f5c2c2]">
                              <Ban size={10} strokeWidth={2} /> {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2.5">
                    {r.active !== false ? (
                      <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#0e8a68]">
                        <CheckCircle2 size={13} strokeWidth={2} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[color:var(--cc-ink-muted)]">
                        <ShieldOff size={13} strokeWidth={2} /> Inactive
                      </span>
                    )}
                  </td>
                  {canWrite && (
                    <td className="px-3 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => setEditing(r)}
                          data-testid={`tenant-tech-profile-edit-${r.id}`}
                          className="p-1.5 hover:bg-[#f5f6f8] rounded-md"
                          title="Edit"
                        >
                          <Pencil size={14} strokeWidth={1.8} />
                        </button>
                        <button
                          onClick={() => setConfirmingDelete(r)}
                          data-testid={`tenant-tech-profile-delete-${r.id}`}
                          className="p-1.5 hover:bg-[#fff5f5] hover:text-[#7a1c1c] rounded-md"
                          title="Delete"
                        >
                          <Trash2 size={14} strokeWidth={1.8} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] flex items-center gap-2 pt-1">
        <ShieldCheck size={12} strokeWidth={1.8} />
        {canWrite
          ? "Edits by CSR, owner, or platform admin are audit-logged with your user ID. Delete removes the profile permanently — prefer flipping to inactive."
          : "Read-only view for your role. To add or edit a technician, ask a CSR, owner, or platform admin."}
        {!anyRowHasAvoidTags && rows.length > 0 && user?.role && (
          <span className="ml-2 italic">Avoid-tag details hidden for your role.</span>
        )}
      </div>

      {editing && (
        <TechProfileFormModal
          tenantSlug={slug}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load_(); }}
        />
      )}
      {confirmingDelete && (
        <TechProfileDeleteConfirm
          tenantSlug={slug}
          profile={confirmingDelete}
          onClose={() => setConfirmingDelete(null)}
          onDeleted={async () => { setConfirmingDelete(null); await load_(); }}
        />
      )}
    </div>
  );
}
